// Plain-language message catalogue. One place holds every user-facing
// validation message so the wording can be reviewed as a unit — same pattern as the upload
// step's error catalogue.

import { formatCountOf, joinWithOr, pluralise } from "utils/formatUtility";
import { lowerFirstLetter } from "utils/stringUtility";
import { describeEitherOrPrompt } from "../../../../shared/describeEitherOrPrompt";
import { DuplicateRow, SkippedRow, SkippedRowReason } from "./validationTypes";

export const messages = {
  required: (field: string) => `${field} is required — this cell can't be left blank.`,
  conditionalRequired: (field: string, trigger: string) => `${field} is required when ${trigger}.`,
  textMaxLength: (field: string, max: number) => `${field} is too long — maximum ${max} characters.`,
  textLengthRange: (field: string, min: number, max: number) =>
    `${field} must be between ${min} and ${max} characters.`,
  wholeNumberRange: (field: string, min: number, max: number) =>
    `${field} must be a whole number between ${min} and ${max}.`,
  decimalRange: (field: string, min: number, max: number) => `${field} must be a number between ${min} and ${max}.`,
  fractionRange: (field: string, min: number, max: number) =>
    `${field} must be a number or a fraction between ${min} and ${max} — for example 0.25 or 1/4.`,
  date: (field: string) => `${field} is not a valid date — use DD/MM/YYYY or YYYY-MM-DD.`,
  email: (field: string) => `${field} is not a valid email address.`,
  phone: (field: string) => `${field} is not a valid phone number — up to 20 characters, digits only.`,
  sortCode: (field: string) => `${field} is not a valid sort code — up to 8 characters, digits only.`,
  allowedValues: (field: string, listLabel: string) => `${field} is not one of the allowed values (${listLabel}).`,
  lookup: (field: string, listLabel: string) => `${field} was not found in ${listLabel}.`,
  lookupSourceMissing: (field: string, listLabel: string) =>
    `${field} can't be checked — ${listLabel} was not provided in this upload.`,
  externalSourceMissing: (field: string, listLabel: string) =>
    `${field} can't be checked — ${listLabel} was not included when that import was submitted.`,
  crossFieldCompare: (field: string, opText: string, otherField: string) => `${field} must be ${opText} ${otherField}.`,
  crossFieldArithmetic: (terms: string[], equalsField: string) => `${terms.join(" + ")} must equal ${equalsField}.`,
  crossRowUnique: (fields: string[], withinFields?: string[]) =>
    withinFields && withinFields.length > 0
      ? `${fields.join(" + ")} must be unique within each ${withinFields.join(" + ")}.`
      : `${fields.join(" + ")} must be unique — this value appears on more than one row.`,
  crossRowSum: (sumField: string, expected: number, actual: number) =>
    `${sumField} must add up to ${expected} for this group — it currently adds up to ${actual}.`,
  crossRowSumMixed: (fields: string[], actual: number) =>
    `This group uses both ${fields.join(
      " and "
    )}, so the shares are added up as percentages — they must come to 100% but currently come to ${actual}%.`,
  exactlyOneOfConflict: (fields: string[]) =>
    `${fields.join(" and ")} can't both be filled in — keep one and clear the other.`,
  exactlyOneOfMissing: (fields: string[]) =>
    fields.length === 1 ? `${fields[0]} is required.` : `${joinWithOr(fields)} is required — fill in one of them.`,
  exactlyOneOfUnmapped: (fields: string[]) => `${describeEitherOrPrompt(fields)} — neither column was mapped.`,
  crossSheetSum: (label: string, worksheets: string[], expected: number, actual: number) =>
    `${label}: the amounts across ${worksheets.join(
      ", "
    )} must net to ${expected} — they currently add up to ${actual}.`,
  // A repeat quotes the values it repeats, so the row can be checked without going to find the
  // one that was kept.
  leftOutReason: (row: SkippedRow) => {
    switch (row.reason) {
      case SkippedRowReason.Keyless:
        return `Nothing in ${joinWithOr(row.blankFields)}`;
      case SkippedRowReason.Duplicate:
        return `Same ${listRowIdentifierValues(row).join(" + ")} as row ${row.duplicateOfRowNumber}`;
      case SkippedRowReason.NoMatchingRow:
        return `No matching row in ${row.mainSheetName}`;
      case SkippedRowReason.BlankJoinKey:
        return `Nothing in ${row.keyColumnName}`;
      case SkippedRowReason.RepeatedJoinKey:
        return `Same ${row.keyColumnName} ${row.keyValue} as row ${row.duplicateOfRowNumber}`;
    }
  },
  leftOutRow: (row: SkippedRow) =>
    `Left out — ${lowerFirstLetter(messages.leftOutReason(row))}, so this row won't be imported.`,
  everyRowLeftOut: (blankFields: string[]) =>
    blankFields.length === 0
      ? "Every row was left out."
      : `Every row was left out — ${blankFields.join(" and ")} ${pluralise(
          blankFields.length,
          "is",
          "are"
        )} blank in every row.`,
  noRowsMatched: (sheetName: string, mainSheetName: string) =>
    `No row on ${sheetName} matches a row on ${mainSheetName}, so nothing from ${sheetName} will be imported.`,
  missingKeyColumn: (sheetName: string, keyColumnName: string | null) =>
    keyColumnName
      ? `${keyColumnName} isn't a column on ${sheetName}, so nothing from ${sheetName} will be imported.`
      : `No key column is chosen for ${sheetName}, so nothing from ${sheetName} will be imported.`,
  missingMainKeyColumn: (mainSheetName: string, keyColumnName: string | null) =>
    keyColumnName
      ? `${keyColumnName} isn't a column on ${mainSheetName}, so no other sheet can be matched to it and nothing from them will be imported.`
      : `No key column is chosen for ${mainSheetName}, so no other sheet can be matched to it and nothing from them will be imported.`,
  mainRowsWithoutMatch: (rowCount: number, mainSheetName: string, sheetName: string) =>
    `${formatCountOf(rowCount, "row")} on ${mainSheetName} ${pluralise(
      rowCount,
      "has",
      "have"
    )} no match on ${sheetName}, so ${pluralise(rowCount, "its", "their")} ${sheetName} columns are blank.`,
  noMatchOnSheet: (sheetName: string) => `No match on ${sheetName}, so its columns are blank.`,
  missingColumn: (field: string, columnLabel: string) =>
    `The column "${columnLabel}" mapped to ${field} was not found in the uploaded file.`,
};

// Each row identifier column with the value the repeat shares, e.g. "Block Number 4".
function listRowIdentifierValues(row: DuplicateRow): string[] {
  return row.rowIdentifierFields.map((field) => `${field} ${row.values[field] ?? ""}`.trim());
}

export const compareOpText: Record<"gte" | "lte" | "eq", string> = {
  gte: "on or after",
  lte: "on or before",
  eq: "equal to",
};

export const compareOpTextNumber: Record<"gte" | "lte" | "eq", string> = {
  gte: "greater than or equal to",
  lte: "less than or equal to",
  eq: "equal to",
};
